import { useEffect, useMemo, useRef, useState } from "react";

import { Layers, Edit, Plus, Trash2 } from "lucide-react";

import { Select } from "antd";

import { Reactable } from "../../components/ui/reactable";

import { Button } from "../../components/ui/button";

import { MASTER_BLOCK, MASTER_FLOOR, EMPLOYEE_DESIGNATION } from "../../constants/api";

import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";

import { DialogModal } from "../../components/ui/dialogModal";

import { Input, Textarea } from "../../components/ui/input";

import useApiMethods from "../../lib/useApiMethods";

import ToastMsg from "../../components/common/ToastMsg";

import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { masterFormSelectProps } from "../../components/common/BranchFilterDropdown";

import { isBranchManagementEnabled } from "../../utils/customConfigSession";
import useBranchRole from "../../hooks/useBranchRole";
import {
    MASTER_BLOCK_FLOOR_NAME_MAX,
    MASTER_NOTES_MAX_LENGTH,
    masterSaveErrorMessage,
    validateMasterLocationName,
} from "./masterLocationFields";



const { Option } = Select;



const FloorMaster = () => {

    const { create, update, fetch } = useApiMethods();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const branchEnabled = isBranchManagementEnabled();
    const { isBranchManager, branchId: loginBranchId } = useBranchRole();
    const lockedBranchId = loginBranchId
        ? (Number(loginBranchId) || loginBranchId)
        : undefined;

    const branchListFilter = useMemo(() => {
        if (isBranchManager && lockedBranchId) {
            return { branch: lockedBranchId };
        }
        return {};
    }, [isBranchManager, lockedBranchId]);

    const [data, setData] = useState([]);

    const [totalRecords, setTotalRecords] = useState(0);

    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);

    const [loading, setLoading] = useState(false);

    const [editMode, setEditMode] = useState(false);

    const [blocks, setBlocks] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [formData, setFormData] = useState({

        id: null,

        code: "",

        floor_name: "",

        block: undefined,

        notes: "",

        is_active: true,

        staffing_thresholds: [],

    });



    const { add: addPermission, edit: editPermission } =

        checkUserPermissionFunc("master", "floor", "SubMenu");



    useEffect(() => {
        fetchRef.current(MASTER_BLOCK, {
            drop_down: "True",
            is_active: "True",
            ...branchListFilter,
        })
            .then((res) => {
                const raw = res?.data?.result || res?.data || [];
                setBlocks(Array.isArray(raw) ? raw : []);
            })
            .catch(() => setBlocks([]));
    }, [serverTrigger, branchListFilter]);

    useEffect(() => {
        fetchRef.current(EMPLOYEE_DESIGNATION, {
            drop_down: "True",
            is_active: "True",
        })
            .then((res) => {
                const raw = res?.data?.result || res?.data || [];
                setDesignations(Array.isArray(raw) ? raw : []);
            })
            .catch(() => setDesignations([]));
    }, []);



    const resetForm = () => {

        setFormData({ id: null, code: "", floor_name: "", block: undefined, notes: "", is_active: true, staffing_thresholds: [] });

        setEditMode(false);

        setOpenDialog(false);

    };



    const openAddDialog = () => {

        setFormData({ id: null, code: "", floor_name: "", block: undefined, notes: "", is_active: true, staffing_thresholds: [] });

        setEditMode(false);

        setOpenDialog(true);

    };



    const columns = useMemo(() => {

        const cols = [

            { accessor: "code", Header: "Code", filterable: true },

            { accessor: "floor_name", Header: "Floor Name", sortable: true, filterable: true },

            { accessor: "block_name", Header: "Block", filterable: true, Cell: ({ value }) => value || "-" },

        ];

        if (branchEnabled) {

            cols.push({
                accessor: "branch_name",
                Header: "Branch",
                filterable: true,
                Cell: ({ value }) => value || "-",
            });

        }

        cols.push(

            { accessor: "notes", Header: "Description", Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description") },

            {

                accessor: "is_active",

                Header: "Status",

                filterable: true,

                Cell: ({ value }) => (

                    <span className={cn(

                        "px-2 py-1 text-xs font-medium rounded-full",

                        value ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"

                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"

                    )}>

                        {value ? "Active" : "Inactive"}

                    </span>

                ),

            }

        );

        if (editPermission) {

            cols.push({

                accessor: "action",

                Header: "Actions",

                sortable: false,

                Cell: ({ row }) => (

                    <Button variant="ghost" size="sm" className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"

                        onClick={() => {

                            setEditMode(true);

                            setFormData({

                                id: row?.id || null,

                                code: row?.code || "",

                                floor_name: row?.floor_name || "",

                                block: row?.block_id || undefined,

                                notes: row?.notes || "",

                                is_active: row?.is_active ?? true,

                                staffing_thresholds: (row?.staffing_thresholds || []).map((item) => ({
                                    designation: item.designation_id || item.designation,
                                    min_count: item.min_count ?? 0,
                                    max_count: item.max_count ?? "",
                                })),

                            });

                            setOpenDialog(true);

                        }}>

                        <Edit className="h-4 w-4" />

                    </Button>

                ),

            });

        }

        return cols;

    }, [branchEnabled, editPermission]);



    const handleSave = async () => {

        if (!formData.floor_name?.trim()) {

            ToastMsg("error", "Floor name is required");

            return;

        }

        const nameErr = validateMasterLocationName(formData.floor_name, "Floor name");
        if (nameErr) {
            ToastMsg("error", nameErr);
            return;
        }

        if (!formData.block) {

            ToastMsg("error", "Block is required");

            return;

        }

        try {

            setLoading(true);

            const payload = {
                ...formData,
                is_draft: false,
                staffing_thresholds: (formData.staffing_thresholds || [])
                    .filter((row) => row.designation)
                    .map((row) => ({
                        designation: row.designation,
                        min_count: Number(row.min_count) || 0,
                        max_count: row.max_count === "" || row.max_count == null ? null : Number(row.max_count),
                    })),
            };

            const response = editMode

                ? await update(MASTER_FLOOR, payload)

                : await create(MASTER_FLOOR, payload);

            if (response?.status === "success") {

                ToastMsg("success", response.message || "Floor saved");

                resetForm();

                setServerTrigger((p) => !p);

            } else {

                ToastMsg("error", masterSaveErrorMessage(response, "Failed to save floor"));

            }

        } finally {

            setLoading(false);

        }

    };



    return (

        <ContainerCard>

            <ModuleTitleCard

                Icon={Layers}

                title="Floor"

                subtitle="Manage floors linked to blocks"

                totalRecords={totalRecords}

                buttonEnable={!!addPermission || !!editPermission}

                setOpenDialog={openAddDialog}

                buttonName="Add Floor"

            />

            <Reactable

                columns={columns}

                data={data}

                setData={setData}

                setTotalCount={setTotalRecords}

                url={MASTER_FLOOR}

                filter={branchListFilter}

                serverSide

                serverTrigger={serverTrigger}

                rowSize={editPermission ? "sm" : null}

                isCSVRequired

                isPDFRequired

                isDateRequired={false}

            />

            <DialogModal

                open={openDialog}

                onClose={resetForm}

                onSave={handleSave}

                title={editMode ? "Edit Floor" : "Add Floor"}

                closeLabel="Clear"

                saveLabel={editMode ? "Update" : "Save"}

                editMode={editMode}

                loading={loading}

                width="560px"

            >

                <div className="space-y-4">

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>

                            Block <span className="text-red-500">*</span>

                        </label>

                        <Select
                            {...masterFormSelectProps}
                            placeholder="Select block"
                            value={formData.block}
                            onChange={(value) => setFormData({ ...formData, block: value })}
                            disabled={loading}
                        >

                            {blocks.map((b) => (

                                <Option key={b.id} value={b.id}>

                                    {b.block_name}{b.branch_name ? ` (${b.branch_name})` : ""}

                                </Option>

                            ))}

                        </Select>

                    </div>

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>

                            Floor Name <span className="text-red-500">*</span>

                        </label>

                        <Input

                            value={formData.floor_name}

                            onChange={(e) => setFormData({ ...formData, floor_name: e.target.value })}

                            placeholder="Enter floor name"

                            maxLength={MASTER_BLOCK_FLOOR_NAME_MAX}

                            disabled={loading}

                        />

                    </div>

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>Description</label>

                        <Textarea

                            value={formData.notes}

                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}

                            placeholder="Optional notes"

                            rows={3}

                            maxLength={MASTER_NOTES_MAX_LENGTH}

                            showCharCount
                            disabled={loading}
                            className={modalTextareaClass}
                        />

                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <label className={modalFieldLabelClass}>Occupancy minima by designation</label>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1"
                                disabled={loading}
                                onClick={() => setFormData({
                                    ...formData,
                                    staffing_thresholds: [
                                        ...(formData.staffing_thresholds || []),
                                        { designation: undefined, min_count: 0, max_count: "" },
                                    ],
                                })}
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Add role
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            These minima show on Employee Occupancy. Below min is marked short.
                        </p>
                        {(formData.staffing_thresholds || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">No designation minima yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {(formData.staffing_thresholds || []).map((row, index) => (
                                    <div key={index} className="grid grid-cols-[1fr_70px_70px_auto] gap-2 items-center">
                                        <Select
                                            {...masterFormSelectProps}
                                            placeholder="Designation"
                                            value={row.designation}
                                            onChange={(value) => {
                                                const next = [...formData.staffing_thresholds];
                                                next[index] = { ...next[index], designation: value };
                                                setFormData({ ...formData, staffing_thresholds: next });
                                            }}
                                            disabled={loading}
                                        >
                                            {designations.map((item) => (
                                                <Option key={item.id} value={item.id}>
                                                    {item.designation_name || item.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        <Input
                                            type="number"
                                            min={0}
                                            placeholder="Min"
                                            value={row.min_count}
                                            onChange={(e) => {
                                                const next = [...formData.staffing_thresholds];
                                                next[index] = { ...next[index], min_count: e.target.value };
                                                setFormData({ ...formData, staffing_thresholds: next });
                                            }}
                                            className={modalInputClass}
                                            disabled={loading}
                                        />
                                        <Input
                                            type="number"
                                            min={0}
                                            placeholder="Max"
                                            value={row.max_count}
                                            onChange={(e) => {
                                                const next = [...formData.staffing_thresholds];
                                                next[index] = { ...next[index], max_count: e.target.value };
                                                setFormData({ ...formData, staffing_thresholds: next });
                                            }}
                                            className={modalInputClass}
                                            disabled={loading}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500"
                                            disabled={loading}
                                            onClick={() => setFormData({
                                                ...formData,
                                                staffing_thresholds: formData.staffing_thresholds.filter((_, i) => i !== index),
                                            })}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label
                            htmlFor="is_active"
                            className={modalFieldLabelClass}
                        >
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                </div>

            </DialogModal>

        </ContainerCard>

    );

};



export default FloorMaster;


